import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = ["searchInput", "results", "resultsList", "loading", "initialMessage", "resultLink"]
  static values = { url: String }

  connect() {
    this.timeout = null
    this.blurTimeout = null
    this.selectedIndex = -1
    this.isSelecting = false
    this.hasSearched = false

    this.clickListener = (ev) => {
      if (!this.element.contains(ev.target) && !this.isSelecting) {
        this.hideResults()
      }
    }

    this.keydownListener = (ev) => {
      if (ev.key === "Escape") {
        this.hideResults()
        this.searchInputTarget.blur()
      } else if (ev.key === "ArrowDown") {
        ev.preventDefault()
        this.navigateResults(1)
      } else if (ev.key === "ArrowUp") {
        ev.preventDefault()
        this.navigateResults(-1)
      } else if (ev.key === "Enter" && this.selectedIndex >= 0) {
        ev.preventDefault()
        this.selectCurrentResult()
      }
    }

    window.addEventListener("click", this.clickListener)
    this.searchInputTarget.addEventListener("keydown", this.keydownListener)
  }

  disconnect() {
    window.removeEventListener("click", this.clickListener)
    if (this.searchInputTarget) {
      this.searchInputTarget.removeEventListener("keydown", this.keydownListener)
    }
  }

  search(event) {
    const query = event.target.value.trim()

    clearTimeout(this.timeout)

    if (query.length < 2) {
      this.hideResults()
      this.hasSearched = false
      return
    }

    this.hasSearched = true
    this.timeout = setTimeout(() => {
      this.performSearch(query)
    }, 300)
  }

  async performSearch(query) {
    this.showLoading()

    const url = `${this.urlValue}?query=${encodeURIComponent(query)}`

    try {
      const response = await fetch(url, {
        headers: {
          Accept: "text/html"
        }
      })

      if (!response.ok) {
        throw new Error("Search failed")
      }

      const html = await response.text()
      const parser = new DOMParser()
      const doc = parser.parseFromString(html, "text/html")
      const resultsFrame = doc.querySelector("turbo-frame#city_search_results")

      if (resultsFrame) {
        this.resultsListTarget.innerHTML = resultsFrame.innerHTML
        this.loadingTarget.classList.add("hidden")
        if (this.hasInitialMessageTarget) {
          this.initialMessageTarget.classList.add("hidden")
        }
        this.selectedIndex = -1
        this.attachResultHandlers()
        this.showResults()
      } else {
        this.hideResults()
      }
    } catch (error) {
      console.error("City search error:", error)
      this.hideResults()
    }
  }

  attachResultHandlers() {
    this.resultLinkTargets.forEach((link, index) => {
      const clickHandler = (ev) => {
        ev.preventDefault()
        ev.stopPropagation()
        this.pickCity(ev)
      }
      link.addEventListener("click", clickHandler, { capture: true })

      link.classList.remove("bg-background-light")
      if (index === this.selectedIndex) {
        link.classList.add("bg-background-light")
      }
    })
  }

  pickCity(event) {
    this.isSelecting = true

    if (this.blurTimeout) {
      clearTimeout(this.blurTimeout)
      this.blurTimeout = null
    }

    const link = event.currentTarget || event.target.closest("[data-city-search-target='resultLink']")
    if (!link) {
      this.isSelecting = false
      return
    }

    const cityName = link.dataset.cityName
    if (cityName) {
      this.searchInputTarget.value = cityName
    }

    this.resultsTarget.classList.add("hidden")
    this.selectedIndex = -1

    setTimeout(() => {
      this.isSelecting = false
    }, 100)
  }

  navigateResults(direction) {
    if (this.resultLinkTargets.length === 0) return

    this.selectedIndex = Math.max(-1, Math.min(this.resultLinkTargets.length - 1, this.selectedIndex + direction))

    this.resultLinkTargets.forEach((link, index) => {
      link.classList.remove("bg-background-light")
      if (index === this.selectedIndex) {
        link.classList.add("bg-background-light")
        link.scrollIntoView({ block: "nearest" })
      }
    })
  }

  selectCurrentResult() {
    if (this.selectedIndex >= 0 && this.resultLinkTargets[this.selectedIndex]) {
      this.resultLinkTargets[this.selectedIndex].click()
    }
  }

  showResults() {
    this.resultsTarget.classList.remove("hidden")
    this.selectedIndex = -1

    if (!this.hasSearched && this.hasInitialMessageTarget) {
      this.initialMessageTarget.classList.remove("hidden")
      this.loadingTarget.classList.add("hidden")
    }
  }

  hideResults() {
    if (this.blurTimeout) {
      clearTimeout(this.blurTimeout)
    }

    this.blurTimeout = setTimeout(() => {
      if (!this.isSelecting) {
        this.resultsTarget.classList.add("hidden")
        this.selectedIndex = -1
      }
    }, 200)
  }

  showLoading() {
    this.loadingTarget.classList.remove("hidden")
    if (this.hasInitialMessageTarget) {
      this.initialMessageTarget.classList.add("hidden")
    }
    this.resultsListTarget.innerHTML = ""
  }
}
